import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  GraduationCap, Award, CalendarDays, RotateCcw, Plus, Loader2, Sparkles,
  Trash2, Pencil, CheckCircle2, ChevronRight,
} from "lucide-react";
import CertifStatsTab from "../components/certif/CertifStatsTab";

const inputCls = "bg-[#0b0811] border-white/10 text-white";
const STATUT_BADGE = {
  preparation: { label: "Préparation", cls: "bg-sky-500/15 text-sky-300" },
  planifiee: { label: "Planifiée", cls: "bg-amber-500/15 text-amber-300" },
  en_cours: { label: "En cours", cls: "bg-emerald-500/15 text-emerald-300" },
  cloturee: { label: "Clôturée", cls: "bg-white/10 text-slate-400" },
};

const certLabel = (code, titre) => (code ? `${code} — ${titre}` : titre);

export default function ExamCertifPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [certs, setCerts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [retakes, setRetakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showCert, setShowCert] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [o, c, s, r] = await Promise.all([
        api.get("/certif/overview"), api.get("/certif/certifications"),
        api.get("/certif/sessions"), api.get("/certif/retakes"),
      ]);
      setOverview(o.data); setCerts(c.data.certifications || []);
      setSessions(s.data.sessions || []); setRetakes(r.data.retakes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="exam-certif-page">
      <PageHeader icon={<GraduationCap />} title="Examens certifiants"
        description="Organisation des sessions d'examen · Sujets & QCM générés par IA depuis votre référentiel · Jury · Délibération · Documents · Rattrapages"
        testId="exam-certif-header" />

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Sessions à venir", value: overview.sessions_a_venir, icon: CalendarDays, color: "#f7c935" },
            { label: "Candidats", value: overview.candidats, icon: GraduationCap, color: "#3b82f6" },
            { label: "Taux de réussite", value: overview.taux_reussite === null ? "—" : `${overview.taux_reussite}%`, icon: Award, color: "#10b981" },
            { label: "Rattrapages en attente", value: overview.rattrapages_en_attente, icon: RotateCcw, color: "#f59e0b" },
          ].map((c, i) => (
            <div key={i} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`exam-stat-${i}`}>
              <c.icon className="w-4 h-4 mb-2" style={{ color: c.color }} />
              <p className="text-2xl font-bold text-white">{c.value}</p>
              <p className="text-xs text-slate-500">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="sessions">
        <TabsList className="bg-[#14101c] border border-white/5">
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="certs" data-testid="tab-certs">Certifications</TabsTrigger>
          <TabsTrigger value="retakes" data-testid="tab-retakes">Rattrapages</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Statistiques</TabsTrigger>
        </TabsList>

        {/* ─── SESSIONS ─── */}
        <TabsContent value="sessions">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setShowNewSession(true)} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="new-session-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle session
            </Button>
          </div>
          {sessions.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-10">Aucune session. Créez votre première session d'examen.</p>
          ) : sessions.map(s => {
            const badge = STATUT_BADGE[s.statut] || STATUT_BADGE.preparation;
            const done = (s.checklist || []).filter(c => c.done).length;
            return (
              <button key={s.session_id} onClick={() => navigate(`/admin/examens/session/${s.session_id}`)}
                className="w-full text-left bg-[#14101c] border border-white/5 rounded-xl p-4 mb-2 hover:border-[#f7c935]/40 transition-colors flex items-center gap-4"
                data-testid={`session-row-${s.session_id}`}>
                <div className="flex-1">
                  <p className="text-white font-bold">{certLabel(s.certification_code, s.certification_titre) || s.certification_code} — {s.date_examen || "date à définir"}</p>
                  <p className="text-xs text-slate-500">{s.lieu || "Lieu à définir"} · Responsable : {s.responsable || "—"} · {s.nb_candidats} candidat(s)</p>
                </div>
                <span className="text-xs text-slate-500">Checklist {done}/{(s.checklist || []).length}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.cls}`}>{badge.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            );
          })}
        </TabsContent>

        {/* ─── CERTIFICATIONS ─── */}
        <TabsContent value="certs">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setShowCert({})} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="new-cert-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle certification
            </Button>
          </div>
          {certs.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-10">Aucune certification. Créez votre référentiel : titre, compétences et barèmes.</p>
          ) : certs.map(c => (
            <div key={c.certification_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-2 flex items-center gap-4" data-testid={`cert-row-${c.certification_id}`}>
              <Award className="w-5 h-5 text-[#f7c935]" />
              <div className="flex-1">
                <p className="text-white font-bold">{certLabel(c.code, c.titre)}</p>
                <p className="text-xs text-slate-500">
                  {c.organisme || "Organisme non renseigné"} · {(c.competences || []).length} compétences · QCM {c.qcm?.nb_questions}q
                  {c.qcm?.generation_ia ? " (génération IA)" : " (document fixe)"} · {c.pratique?.nb_sujets} sujets pratiques
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setShowCert(c)} data-testid={`edit-cert-${c.certification_id}`}><Pencil className="w-4 h-4" /></Button>
            </div>
          ))}
        </TabsContent>

        {/* ─── RATTRAPAGES ─── */}
        <TabsContent value="retakes">
          {retakes.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-10">Aucun candidat en rattrapage.</p>
          ) : retakes.map(r => (
            <div key={r.retake_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-2 flex items-center gap-4" data-testid={`retake-row-${r.retake_id}`}>
              <RotateCcw className="w-4 h-4 text-[#f59e0b]" />
              <div className="flex-1">
                <p className="text-white font-bold">{r.nom} {r.prenom} — {r.certification_code || r.certification_titre}</p>
                <p className="text-xs text-slate-500">
                  À repasser : {(r.epreuves_a_repasser || []).join(" + ") || "—"} · Épreuves conservées : {(r.epreuves_validees || []).join(", ") || "aucune"} · Échéance : {r.deadline}
                </p>
              </div>
              <select value={r.statut} className="bg-[#0b0811] border border-white/10 rounded-lg text-xs text-white px-2 py-1.5"
                onChange={async (e) => {
                  await api.put(`/certif/retakes/${r.retake_id}`, { statut: e.target.value });
                  toast.success("Statut mis à jour"); reload();
                }} data-testid={`retake-status-${r.retake_id}`}>
                <option value="en_attente">En attente</option>
                <option value="replanifie">Replanifié</option>
                <option value="certifie">Certifié (rattrapage réussi)</option>
                <option value="abandonne">Abandonné</option>
              </select>
            </div>
          ))}
        </TabsContent>

        {/* ─── STATISTIQUES ─── */}
        <TabsContent value="stats">
          <CertifStatsTab api={api} />
        </TabsContent>
      </Tabs>

      {showNewSession && <NewSessionDialog api={api} certs={certs} onClose={() => setShowNewSession(false)} onCreated={(id) => navigate(`/admin/examens/session/${id}`)} />}
      {showCert !== null && <CertDialog api={api} existing={showCert.certification_id ? showCert : null} onClose={() => { setShowCert(null); reload(); }} />}
    </div>
  );
}

function NewSessionDialog({ api, certs, onClose, onCreated }) {
  const [form, setForm] = useState({ certification_id: certs[0]?.certification_id || "", date_examen: "", lieu: "", responsable: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.certification_id) { toast.error("Choisissez une certification (créez-en une si la liste est vide)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/certif/sessions", form);
      toast.success("Session créée avec sa checklist J-15 → J+15");
      onCreated(data.session_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="new-session-dialog">
        <DialogHeader><DialogTitle>Nouvelle session d'examen</DialogTitle></DialogHeader>
        <label className="text-xs text-slate-400">Certification</label>
        <select value={form.certification_id} onChange={e => setForm({ ...form, certification_id: e.target.value })}
          className="bg-[#0b0811] border border-white/10 rounded-lg text-sm text-white px-3 py-2" data-testid="session-cert-select">
          {certs.length === 0 && <option value="">— Créez d'abord une certification —</option>}
          {certs.map(c => <option key={c.certification_id} value={c.certification_id}>{certLabel(c.code, c.titre)}</option>)}
        </select>
        <label className="text-xs text-slate-400">Date de l'examen</label>
        <Input type="date" className={inputCls} value={form.date_examen} onChange={e => setForm({ ...form, date_examen: e.target.value })} data-testid="session-date-input" />
        <label className="text-xs text-slate-400">Lieu</label>
        <Input className={inputCls} placeholder="Adresse du centre d'examen" value={form.lieu} onChange={e => setForm({ ...form, lieu: e.target.value })} data-testid="session-lieu-input" />
        <label className="text-xs text-slate-400">Responsable de session</label>
        <Input className={inputCls} value={form.responsable} onChange={e => setForm({ ...form, responsable: e.target.value })} data-testid="session-responsable-input" />
        <Button onClick={submit} disabled={saving} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="session-create-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer la session"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CertDialog({ api, existing, onClose }) {
  const [form, setForm] = useState(existing ? { ...existing } : {
    code: "", titre: "", organisme: "", materiel: "", consignes_pratique: "", prerequis: "",
    competences: [], qcm: { actif: true, generation_ia: true, nb_questions: 40, duree_min: 40, points_par_question: 0.5, bareme: 20, seuil: 10 },
    pratique: { nb_sujets: 3, duree_min: 180, seuil_points: 0 },
  });
  const [saving, setSaving] = useState(false);
  const setComp = (i, key, val) => {
    const comps = [...(form.competences || [])];
    comps[i] = { ...comps[i], [key]: key === "points_max" ? Number(val) : val };
    setForm({ ...form, competences: comps });
  };
  const submit = async () => {
    if (!form.titre) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      if (existing) await api.put(`/certif/certifications/${existing.certification_id}`, form);
      else await api.post("/certif/certifications", form);
      toast.success("Certification enregistrée"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer cette certification ?")) return;
    await api.delete(`/certif/certifications/${existing.certification_id}`);
    toast.success("Certification supprimée"); onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cert-dialog">
        <DialogHeader><DialogTitle>{existing ? `Modifier — ${existing.titre}` : "Nouvelle certification"}</DialogTitle></DialogHeader>
        <label className="text-xs text-slate-400">Titre de la certification *</label>
        <Input className={inputCls} value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} data-testid="cert-titre-input" />
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-400">Référence (optionnel)</label><Input className={inputCls} placeholder="Référence interne ou officielle" value={form.code || ""} onChange={e => setForm({ ...form, code: e.target.value })} data-testid="cert-code-input" /></div>
          <div><label className="text-xs text-slate-400">Organisme certificateur</label><Input className={inputCls} value={form.organisme || ""} onChange={e => setForm({ ...form, organisme: e.target.value })} data-testid="cert-organisme-input" /></div>
        </div>
        <label className="text-xs text-slate-400">Prérequis d'admission (affichés dans le dossier d'inscription et le règlement)</label>
        <Textarea className={inputCls} rows={2} value={form.prerequis || ""} onChange={e => setForm({ ...form, prerequis: e.target.value })} data-testid="cert-prerequis-input" />
        <label className="text-xs text-slate-400">Contexte métier / matériel disponible (utilisé par l'IA pour générer sujets et QCM)</label>
        <Textarea className={inputCls} rows={3} value={form.materiel || ""} onChange={e => setForm({ ...form, materiel: e.target.value })} data-testid="cert-materiel-input" />

        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-2">Épreuve 1 — QCM</p>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="text-xs text-slate-400">Nb questions</label><Input type="number" className={inputCls} value={form.qcm?.nb_questions ?? 40} onChange={e => setForm({ ...form, qcm: { ...form.qcm, nb_questions: Number(e.target.value) } })} /></div>
          <div><label className="text-xs text-slate-400">Durée (min)</label><Input type="number" className={inputCls} value={form.qcm?.duree_min ?? 40} onChange={e => setForm({ ...form, qcm: { ...form.qcm, duree_min: Number(e.target.value) } })} /></div>
          <div><label className="text-xs text-slate-400">Seuil (/{form.qcm?.bareme ?? 20})</label><Input type="number" className={inputCls} value={form.qcm?.seuil ?? 10} onChange={e => setForm({ ...form, qcm: { ...form.qcm, seuil: Number(e.target.value) } })} /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-white/10 p-2.5">
          <span className="text-sm text-white flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#f7c935]" /> Génération IA du QCM à chaque session</span>
          <Switch checked={!!form.qcm?.generation_ia} onCheckedChange={v => setForm({ ...form, qcm: { ...form.qcm, generation_ia: v } })} data-testid="cert-qcm-ia-toggle" />
        </div>

        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-2">Épreuve 2 — Mise en situation</p>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="text-xs text-slate-400">Nb sujets</label><Input type="number" className={inputCls} value={form.pratique?.nb_sujets ?? 3} onChange={e => setForm({ ...form, pratique: { ...form.pratique, nb_sujets: Number(e.target.value) } })} /></div>
          <div><label className="text-xs text-slate-400">Durée (min)</label><Input type="number" className={inputCls} value={form.pratique?.duree_min ?? 180} onChange={e => setForm({ ...form, pratique: { ...form.pratique, duree_min: Number(e.target.value) } })} /></div>
          <div><label className="text-xs text-slate-400">Seuil (points)</label><Input type="number" className={inputCls} value={form.pratique?.seuil_points ?? 0} onChange={e => setForm({ ...form, pratique: { ...form.pratique, seuil_points: Number(e.target.value) } })} data-testid="cert-seuil-pratique" /></div>
        </div>

        <div className="flex items-center justify-between mt-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Compétences du référentiel</p>
          <Button size="sm" variant="outline" className="border-white/10 text-slate-300 h-7 text-xs"
            onClick={() => setForm({ ...form, competences: [...(form.competences || []), { code: `C${(form.competences || []).length + 1}`, libelle: "", points_max: 4 }] })}
            data-testid="cert-add-comp-btn">
            <Plus className="w-3 h-3 mr-1" /> Ajouter
          </Button>
        </div>
        {(form.competences || []).map((c, i) => (
          <div key={i} className="flex gap-2">
            <Input className={`${inputCls} w-16`} value={c.code} onChange={e => setComp(i, "code", e.target.value)} />
            <Input className={`${inputCls} flex-1`} value={c.libelle} onChange={e => setComp(i, "libelle", e.target.value)} placeholder="Libellé de la compétence" />
            <Input type="number" className={`${inputCls} w-20`} value={c.points_max} onChange={e => setComp(i, "points_max", e.target.value)} title="Points max" />
            <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, competences: form.competences.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5 text-rose-400" /></Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button onClick={submit} disabled={saving} className="flex-1 bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="cert-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Enregistrer</>}
          </Button>
          {existing && <Button variant="outline" onClick={remove} className="border-rose-500/30 text-rose-400" data-testid="cert-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
